import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import SearchableSelect from '../../shared/ConditionsDropdown/components/SearchableSelect';
import { getHostTask, getMilestoneAnchor } from '../utils/milestoneUtils';

/**
 * Milestone configuration modal — name, host mission, trigger side.
 */
const MilestoneEditor = ({
  task,
  allTasks = [],
  isOpen,
  onClose,
  onRename,
  onAnchorChange,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const anchor = getMilestoneAnchor(task);
  const host = getHostTask(task, allTasks);
  const hostOptions = allTasks
    .filter((t) => t.nodeType !== 'milestone' && t.id !== task.id)
    .map((t) => ({ ...t, name: t.name || 'Untitled mission' }));

  return createPortal(
    <>
      <div className="milestone-editor-backdrop" onClick={onClose} role="presentation" />
      <div className="milestone-editor" role="dialog" aria-label="Edit milestone" onMouseDown={(e) => e.stopPropagation()}>
        <div className="milestone-editor__header">
          <span className="milestone-editor__title">
            <span className="milestone-editor__title-flag" aria-hidden="true">🚩</span>
            Milestone
          </span>
          <button type="button" className="milestone-editor__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <label className="milestone-editor__field">
          <span className="milestone-editor__label">Name</span>
          <input
            type="text"
            className="milestone-editor__input"
            value={task.name || ''}
            placeholder="Milestone name"
            autoFocus
            onChange={(e) => onRename?.(task.id, e.target.value)}
          />
        </label>

        <div className="milestone-editor__field">
          <span className="milestone-editor__label">Attached mission</span>
          <SearchableSelect
            value={anchor?.hostTaskId || ''}
            options={hostOptions}
            placeholder="Select a mission…"
            allowClear={false}
            onChange={(hostTaskId) => {
              if (!hostTaskId) return;
              onAnchorChange?.(task.id, {
                hostTaskId,
                side: anchor?.side || 'input',
              });
            }}
          />
          {host && (
            <p className="milestone-editor__hint">
              Lines will route through this milestone on the {anchor?.side === 'output' ? 'output' : 'input'} side of &ldquo;{host.name || 'Untitled'}&rdquo;.
            </p>
          )}
        </div>

        <div className="milestone-editor__section">
          <span className="milestone-editor__label">Trigger</span>
          <div className="milestone-editor__side-toggle">
            <button
              type="button"
              className={`milestone-editor__side-btn${(!anchor?.side || anchor.side === 'input') ? ' milestone-editor__side-btn--active' : ''}`}
              disabled={!anchor?.hostTaskId}
              onClick={() => anchor?.hostTaskId && onAnchorChange?.(task.id, { hostTaskId: anchor.hostTaskId, side: 'input' })}
            >
              When mission is ready
            </button>
            <button
              type="button"
              className={`milestone-editor__side-btn${anchor?.side === 'output' ? ' milestone-editor__side-btn--active' : ''}`}
              disabled={!anchor?.hostTaskId}
              onClick={() => anchor?.hostTaskId && onAnchorChange?.(task.id, { hostTaskId: anchor.hostTaskId, side: 'output' })}
            >
              When mission is complete
            </button>
          </div>
          <p className="milestone-editor__hint">
            Passive marker only — does not block or change dependencies.
          </p>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default MilestoneEditor;
